"use client";

import {
  motion,
  useInView,
  AnimatePresence,
  animate,
  useMotionValue,
} from "framer-motion";
import React, { useEffect, useState, useRef, useMemo } from "react";

const Digit = ({ value }: { value: number }) => {
  return (
    <AnimatePresence mode="popLayout">
      <motion.span
        key={value}
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -20, opacity: 0 }}
        transition={{ duration: 0.25 }}
        style={{ display: "inline-block", minWidth: "0.6em" }}
      >
        {value}
      </motion.span>
    </AnimatePresence>
  );
};

function Counter({
  from = 0,
  to,
  duration = 1.6,
  decimals = 0,
  prefix = "",
  suffix = "",
  className = "",
}: {
  from?: number;
  to: number;
  duration?: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const isInView = useInView(ref, { once: true, margin: "-10% 0px" });

  const mv = useMotionValue(from);
  const [value, setValue] = useState<number>(from);

  useEffect(() => {
    const unsub = mv.on("change", (v) => setValue(v));
    return () => unsub();
  }, [mv]);

  useEffect(() => {
    if (!isInView) return;
    const controls = animate(mv, to, { duration, ease: "easeOut" });
    return () => controls.stop();
  }, [isInView, mv, to, duration]);

  const formatted = useMemo(() => {
    const n = decimals ? Number(value).toFixed(decimals) : Math.round(value);
    // Use locale formatting for integers
    return decimals ? n : Number(n).toLocaleString();
  }, [value, decimals]);

  return (
    <motion.span ref={ref} className={className}>
      {prefix}
      {formatted}
      {suffix}
    </motion.span>
  );
}

const AboutSection = () => {
  const [count, setCount] = useState(0);
  const targetCount = 650;

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  // Counter animation only when in view
  useEffect(() => {
    if (isInView) {
      let frame: number;
      let current = 0;
      const step = () => {
        current += 5;
        if (current <= targetCount) {
          setCount(current);
          frame = requestAnimationFrame(step);
        }
      };
      frame = requestAnimationFrame(step);
      return () => cancelAnimationFrame(frame);
    }
  }, [isInView]);

  // Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2, delayChildren: 0.3 },
    },
  };

  const itemVariants = {
    hidden: { y: 40, opacity: 0, scale: 0.95 },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: { type: "spring", damping: 14, stiffness: 120 },
    },
  };

  return (
    <section className="about-section" ref={ref}>
      <motion.div
        className="about-container"
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        {/* Header */}
        <div className="section-header">
          <motion.div className="section-header-title" variants={itemVariants}>
            <div className="heading-04">Who we are -</div>
          </motion.div>

          <motion.div className="section-header-text" variants={itemVariants}>
            <div className="heading-03 cap">
              Transforming your digital Brand with an impactful design branding
              to drive success.
            </div>
            <div className="body-02 gray-color mt-20">
              We specialize in transforming your digital brand through impactful
              design and strategic branding. Our creative approach ensures your
              brand stands out, connects, and converts.
            </div>
          </motion.div>
        </div>

        {/* Bottom Section */}
        <div className="about-bottom">
          <motion.div className="state-card" variants={itemVariants}>
            <div className="heading-04">
              Successfully built brands experience that drives real success.
            </div>
            <div className="state-card-text-wrap">
              <div
                className="statistics-number-wrap _03       heading-01 
      font-bold leading-none 
      text-[20vw]   /* mobile: fills screen width */
      sm:text-[6rem]  /* desktop: your original size */
"
                style={{
                  fontSize: "6rem",
                  fontWeight: "700",
                  lineHeight: "1",
                  paddingBottom: "99px",
                  color: "rgba(255, 255, 255, 0.5)",
                  backgroundImage: "linear-gradient(to top, black, white)", // bottom → top
                  WebkitBackgroundClip: "text",
                }}
              >
                <Counter to={32} duration={1.4} className="text-center" />
              </div>

              <div className="body-02 op-60 mt-4">Successful Project</div>
            </div>
          </motion.div>

          <motion.div
            className="about-img-wrap"
            variants={itemVariants}
            whileHover={{ scale: 1.03 }}
            transition={{ type: "spring", stiffness: 100 }}
          >
            <motion.img
              src="/home-sec1.webp"
              alt="About us"
              className="full-img"
              initial={{ scale: 1.1, opacity: 0 }}
              animate={isInView ? { scale: 1, opacity: 1 } : {}}
              transition={{ duration: 1, ease: "easeOut" }}
            />
            <div className="image-overlay"></div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default AboutSection;
